// 123 R161 -- autosave reassurance
//
// The Events editor uses inline autosave-on-blur across many tiny components
// nested 4-5 levels deep. The BA reported wanting an explicit Save affordance
// "to make sure it is locked in before publishing". Rather than thread a
// dirty/save-callback prop through every editor, components broadcast a
// document event when they successfully PATCH. The EventHeader subscribes
// and renders a "Saved · HH:MM" / "Saving…" pill near the Publish button.
//
// Scope: portal-wide save reassurance, but currently only event-edit surfaces
// notify. Other editors can opt in by calling notifySaved() / notifySaving()
// in their save flow.

import { useEffect, useState } from "react";

type Phase = "idle" | "saving" | "saved";

const SAVED_EVENT = "dugads:edit-saved";
const SAVING_EVENT = "dugads:edit-saving";

export function notifySaving(): void {
  if (typeof document === "undefined") return;
  document.dispatchEvent(new CustomEvent(SAVING_EVENT));
}

export function notifySaved(): void {
  if (typeof document === "undefined") return;
  document.dispatchEvent(new CustomEvent(SAVED_EVENT));
}

export interface SaveStatus {
  phase: Phase;
  /** Epoch ms of the most recent successful save, or null if never saved this session. */
  lastSavedAt: number | null;
}

export function useSaveStatus(): SaveStatus {
  const [phase, setPhase] = useState<Phase>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  useEffect(() => {
    function onSaving() { setPhase("saving"); }
    function onSaved() {
      setLastSavedAt(Date.now());
      setPhase("saved");
    }
    document.addEventListener(SAVING_EVENT, onSaving);
    document.addEventListener(SAVED_EVENT, onSaved);
    return () => {
      document.removeEventListener(SAVING_EVENT, onSaving);
      document.removeEventListener(SAVED_EVENT, onSaved);
    };
  }, []);

  return { phase, lastSavedAt };
}
